import Layout from "@/components/Layout";
import { useMemo, useCallback, useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_CLIPS_BY_LIBRARY_ID, GET_MY_LIBRARY } from "@/queries/queries";
import { MyClipsByLibraryIdQuery, MyClipsByLibraryIdQueryVariables, MyLibraryQuery, MyLibraryQueryVariables } from "@/generated/graphql";
import { useSceneDetection } from "@/hooks/use-scene-detection";
import { Loader2 } from "lucide-react";
// Grandfathered legacy Video: internal debug page, browser <Player> playback
// only — never rendered headlessly. New code must use Video from
// '@remotion/media' — see CLAUDE.md § "Remotion Video Components".
// eslint-disable-next-line no-restricted-imports
import { AbsoluteFill, Video, Sequence, useCurrentFrame, useVideoConfig } from "remotion";
import { Player, PlayerRef } from "@remotion/player";

// ----- Color palette -----
const FACE_COLORS = [
  "#FF3B30", "#007AFF", "#34C759", "#FF9500", "#AF52DE",
  "#FF2D55", "#5AC8FA", "#FFCC00", "#00C7BE", "#FF6482",
];

function buildFaceColorMap(sceneDetection: any): Map<string, string> {
  const map = new Map<string, string>();
  if (!sceneDetection) return map;
  const data = Array.isArray(sceneDetection) ? sceneDetection : [sceneDetection];
  const scenes = data.flatMap((d: any) => d.scenes || []);
  for (const scene of scenes) {
    for (const face of (scene.faces || [])) {
      if (face.face_id && !map.has(face.face_id)) {
        map.set(face.face_id, FACE_COLORS[map.size % FACE_COLORS.length]);
      }
    }
  }
  return map;
}

// ----- Canvas-based face crop with per-face seek times -----
interface FaceCropInput {
  face_id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  seekTime: number;
}

const CROP_SIZE = 160;

function useFaceCropsFromVideo(
  videoUrl: string | undefined | null,
  faces: FaceCropInput[],
): Record<string, string> {
  const [crops, setCrops] = useState<Record<string, string>>({});
  const prevKeyRef = useRef('');

  const key = `${videoUrl}|${faces.map(f => `${f.face_id}@${f.seekTime.toFixed(1)}`).join(',')}`;

  useEffect(() => {
    if (!videoUrl || faces.length === 0 || key === prevKeyRef.current) return;
    prevKeyRef.current = key;

    let cancelled = false;
    const result: Record<string, string> = {};

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = 'auto';

    const canvas = document.createElement('canvas');
    canvas.width = CROP_SIZE;
    canvas.height = CROP_SIZE;
    const ctx = canvas.getContext('2d');

    // Group faces by seek time (quantized to 0.1s) for sequential processing
    const groups = new Map<number, FaceCropInput[]>();
    for (const face of faces) {
      const t = Math.round(face.seekTime * 10) / 10;
      if (!groups.has(t)) groups.set(t, []);
      groups.get(t)!.push(face);
    }
    const seekTimes = Array.from(groups.keys()).sort((a, b) => a - b);
    let groupIdx = 0;

    const processGroup = () => {
      if (cancelled || !ctx || groupIdx >= seekTimes.length) {
        if (!cancelled) {
          video.src = '';
          video.load();
          setCrops({ ...result });
        }
        return;
      }

      const t = seekTimes[groupIdx];
      const groupFaces = groups.get(t)!;

      video.addEventListener('seeked', () => {
        if (cancelled) return;

        for (const face of groupFaces) {
          const pad = 0.6;
          const cx = face.x + face.width / 2;
          const cy = face.y + face.height / 2;
          const ew = face.width * (1 + pad);
          const eh = face.height * (1 + pad);
          const ex = Math.max(0, cx - ew / 2);
          const ey = Math.max(0, cy - eh / 2);
          const sx = Math.round(ex * video.videoWidth);
          const sy = Math.round(ey * video.videoHeight);
          const sw = Math.min(Math.round(ew * video.videoWidth), video.videoWidth - sx);
          const sh = Math.min(Math.round(eh * video.videoHeight), video.videoHeight - sy);

          if (sw <= 0 || sh <= 0) continue;

          ctx.clearRect(0, 0, CROP_SIZE, CROP_SIZE);
          ctx.drawImage(video, sx, sy, sw, sh, 0, 0, CROP_SIZE, CROP_SIZE);

          try {
            result[face.face_id] = canvas.toDataURL('image/jpeg', 0.85);
          } catch {
            // Canvas tainted — CORS not available
          }
        }

        groupIdx++;
        processGroup();
      }, { once: true });

      video.currentTime = t;
    };

    video.addEventListener('loadeddata', () => {
      if (!cancelled) processGroup();
    }, { once: true });

    video.addEventListener('error', () => {
      if (!cancelled) setCrops({});
    }, { once: true });

    video.src = videoUrl;

    return () => {
      cancelled = true;
      video.src = '';
      video.load();
    };
  }, [key]);

  return crops;
}

// ----- Types -----
interface ParsedFace {
  face_id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  is_speaking?: boolean;
  is_main_speaker?: boolean;
}

interface ParsedScene {
  start: number;
  end: number;
  faces: ParsedFace[];
}

function parseScenes(sceneDetection: any): ParsedScene[] {
  if (!sceneDetection) return [];
  const data = Array.isArray(sceneDetection) ? sceneDetection : [sceneDetection];
  return data
    .flatMap((d: any) => d.scenes || [])
    .filter((s: any) => typeof s.start_time === 'number' && typeof s.end_time === 'number')
    .map((s: any) => ({
      start: s.start_time,
      end: s.end_time,
      faces: (s.faces || []).filter((f: any) => f.face_id),
    }))
    .sort((a, b) => a.start - b.start);
}

// Binary search: find the scene containing `time`, or -1
function findSceneIndex(scenes: ParsedScene[], time: number): number {
  let lo = 0;
  let hi = scenes.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const s = scenes[mid];
    if (time < s.start) {
      hi = mid - 1;
    } else if (time > s.end) {
      lo = mid + 1;
    } else {
      return mid;
    }
  }
  return -1;
}


// ----- Remotion Composition -----
interface DebugCompositionProps {
  videoUrl: string;
  startTime: number;
  endTime: number;
  scenes: ParsedScene[];
  faceColorMap: Record<string, string>;
}

const EMPTY_FACES: ParsedFace[] = [];

function getFaceStatusColor(face: ParsedFace): string {
  if (face.is_main_speaker) return '#22C55E'; // green
  if (face.is_speaking) return '#EAB308'; // yellow
  return '#9CA3AF'; // gray
}

const DebugComposition: React.FC<DebugCompositionProps> = ({
  videoUrl,
  startTime,
  endTime,
  scenes,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Convert composition frame to absolute video time, quantized to 0.1s detection intervals
  const absoluteTime = Math.round((startTime + frame / fps) * 10) / 10;

  // Find current scene via binary search — show faces exactly as stored in the detection JSON
  const sceneIdx = findSceneIndex(scenes, absoluteTime);
  const currentFaces: ParsedFace[] = sceneIdx === -1 ? EMPTY_FACES : scenes[sceneIdx].faces;

  const sfrom = Math.round(startTime * fps);
  const eat = Math.round(endTime * fps);
  const dur = Math.max(1, eat - sfrom);

  return (
    <AbsoluteFill style={{ backgroundColor: '#000' }}>
      {/* Video — 16:9, object-fit contain */}
      <Sequence from={0} durationInFrames={dur}>
        <Video
          src={videoUrl}
          startFrom={sfrom}
          endAt={eat}
          pauseWhenBuffering
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </Sequence>

      {/* Face annotations — positioned as percentage of the video frame */}
      {currentFaces.map((face, index) => {
        const statusColor = getFaceStatusColor(face);
        return (
          <div
            key={`${face.face_id}-${index}`}
            style={{
              position: 'absolute',
              left: `${face.x * 100}%`,
              top: `${face.y * 100}%`,
              width: `${face.width * 100}%`,
              height: `${face.height * 100}%`,
              border: `3px solid ${statusColor}`,
              borderRadius: 4,
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }}
          >
            {/* Label */}
            <div
              style={{
                position: 'absolute',
                top: -22,
                left: 0,
                background: statusColor,
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'monospace',
                padding: '1px 6px',
                borderRadius: 3,
                whiteSpace: 'nowrap',
                lineHeight: '18px',
              }}
            >
              {face.face_id}
              {face.is_main_speaker ? ' [main]' : ''}
              {face.is_speaking ? ' [speaking]' : ''}
            </div>
          </div>
        );
      })}

      {/* Time / face count HUD */}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          background: 'rgba(0,0,0,0.7)',
          color: '#fff',
          fontSize: 12,
          fontFamily: 'monospace',
          padding: '3px 8px',
          borderRadius: 4,
          pointerEvents: 'none',
        }}
      >
        t={absoluteTime.toFixed(1)}s | faces={currentFaces.length}
      </div>
    </AbsoluteFill>
  );
};

// ----- Clip Card -----
interface ClipDebugCardProps {
  clip: MyClipsByLibraryIdQuery['myClips'][0];
  videoUrl: string;
  scenes: ParsedScene[];
  faceColorMap: Record<string, string>;
  onPlay: (clipId: string, pause: () => void) => void;
}

const ClipDebugCard: React.FC<ClipDebugCardProps> = ({ clip, videoUrl, scenes, faceColorMap }) => {
  const playerRef = useRef<PlayerRef>(null);

  const segments = (clip as any)?.data?.segments;
  const startTime = segments?.[0]?.start ?? clip.clip_start_time ?? 0;
  const endTime = segments?.[0]?.end ?? clip.clip_end_time ?? 0;

  const fps = 30;
  const duration = Math.max(0, endTime - startTime);
  const durationInFrames = Math.max(1, Math.ceil(duration * fps));

  // Filter scenes to those overlapping this clip's time range
  const clipScenes = useMemo(() => {
    return scenes.filter(s => s.end > startTime && s.start < endTime);
  }, [scenes, startTime, endTime]);

  // Unique faces and their representative scene (first occurrence) — used for seek time
  const clipFaceEntries = useMemo(() => {
    const seen = new Map<string, { face: ParsedFace; sceneStart: number; sceneEnd: number }>();
    for (const scene of clipScenes) {
      for (const face of scene.faces) {
        if (!seen.has(face.face_id)) {
          seen.set(face.face_id, { face, sceneStart: scene.start, sceneEnd: scene.end });
        }
      }
    }
    return Array.from(seen.values());
  }, [clipScenes]);

  // Build face crop inputs with per-face seek times
  const faceCropInputs = useMemo<FaceCropInput[]>(() =>
    clipFaceEntries.map(({ face, sceneStart, sceneEnd }) => ({
      face_id: face.face_id,
      x: face.x,
      y: face.y,
      width: face.width,
      height: face.height,
      seekTime: (sceneStart + sceneEnd) / 2,
    })),
    [clipFaceEntries],
  );
  const faceCrops = useFaceCropsFromVideo(videoUrl, faceCropInputs);

  return (
    <div className="bg-card border border-border/40 rounded-lg overflow-hidden">
      {/* Remotion Player — 16:9 */}
      <div className="relative aspect-video bg-black">
        <Player
          ref={playerRef}
          component={DebugComposition}
          inputProps={{
            videoUrl,
            startTime,
            endTime,
            scenes: clipScenes,
            faceColorMap,
          }}
          durationInFrames={durationInFrames}
          compositionWidth={1920}
          compositionHeight={1080}
          fps={fps}
          autoPlay={false}
          loop
          controls
          clickToPlay
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Info panel */}
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold text-sm truncate">
            {clip.name || `Clip ${clip.id}`}
          </h3>
          <span className="text-xs text-muted-foreground font-mono shrink-0">
            {startTime.toFixed(2)}s – {endTime.toFixed(2)}s ({duration.toFixed(1)}s)
          </span>
        </div>

        {/* Face cards with canvas-cropped thumbnails */}
        {clipFaceEntries.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {clipFaceEntries.map(({ face }) => {
              const statusColor = getFaceStatusColor(face);
              const cropUrl = faceCrops[face.face_id];
              return (
                <div
                  key={face.face_id}
                  className="rounded-lg border-2 overflow-hidden flex flex-col items-center"
                  style={{ borderColor: statusColor }}
                >
                  {cropUrl ? (
                    <img src={cropUrl} className="w-20 h-20 object-cover" />
                  ) : (
                    <div className="w-20 h-20 bg-muted/30 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex flex-col items-center py-1 px-2">
                    <span className="text-[11px] font-mono font-bold" style={{ color: statusColor }}>
                      {face.face_id}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {face.is_main_speaker ? 'main' : face.is_speaking ? 'speaking' : 'detected'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">No faces detected in this clip range</span>
        )}
      </div>
    </div>
  );
};

// ----- Page -----
const LibraryDebug = () => {
  const { libraryId } = useParams<{ libraryId: string }>();
  const playingClipRef = useRef<{ id: string; pause: () => void } | null>(null);

  const { data: libraryData, loading: libraryLoading } = useQuery<MyLibraryQuery, MyLibraryQueryVariables>(
    GET_MY_LIBRARY,
    { variables: { id: libraryId! }, skip: !libraryId }
  );

  const { data: clipsData, loading: clipsLoading } = useQuery<MyClipsByLibraryIdQuery, MyClipsByLibraryIdQueryVariables>(
    GET_CLIPS_BY_LIBRARY_ID,
    { variables: { library_id: libraryId!, limit: 200, offset: 0, sort: 'clip_order:asc' }, skip: !libraryId }
  );

  const library = libraryData?.myLibrary;
  const clips = clipsData?.myClips ?? [];
  const videoUrl = library?.compressed_cut_video_url || library?.compressed_base_video_url || library?.cut_video_url || library?.base_video_url || '';

  const { data: sceneDetection, loading: sceneLoading } = useSceneDetection(
    library?.scene_detection_url,
    library?.scene_detection
  );

  // Stable color map
  const faceColorMap = useMemo(() => {
    const map = buildFaceColorMap(sceneDetection);
    const obj: Record<string, string> = {};
    map.forEach((v, k) => { obj[k] = v; });
    return obj;
  }, [sceneDetection]);

  // All parsed scenes
  const allScenes = useMemo(() => parseScenes(sceneDetection), [sceneDetection]);

  // Sort clips
  const sortedClips = useMemo(() => {
    return [...clips].sort((a, b) => {
      const orderA = a.clip_order ?? Infinity;
      const orderB = b.clip_order ?? Infinity;
      if (orderA !== orderB) return orderA - orderB;
      return (b.clip_score ?? 0) - (a.clip_score ?? 0);
    });
  }, [clips]);

  const handlePlay = useCallback((clipId: string, pause: () => void) => {
    if (playingClipRef.current && playingClipRef.current.id !== clipId) {
      playingClipRef.current.pause();
    }
    playingClipRef.current = { id: clipId, pause };
  }, []);

  const isLoading = libraryLoading || clipsLoading || sceneLoading;

  return (
    <Layout>
      <div className="p-6 max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold">Speaker Detection Debug</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Library: {library?.name || libraryId} | {clips.length} clips
          </p>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && !sceneDetection && (
          <div className="text-center py-20 text-muted-foreground">
            No scene detection data available for this library.
          </div>
        )}

        {/* Clips grid — 16:9 cards */}
        {!isLoading && sceneDetection && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sortedClips.map((clip) => (
              <ClipDebugCard
                key={clip.id}
                clip={clip}
                videoUrl={videoUrl}
                scenes={allScenes}
                faceColorMap={faceColorMap}
                onPlay={handlePlay}
              />
            ))}
          </div>
        )}

        {!isLoading && clips.length === 0 && (
          <div className="text-center py-20 text-muted-foreground">
            No clips found for this library.
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LibraryDebug;
